import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { localNum } from "@faire/web--source/common/localization/localize";
import { Button } from "@faire/web--source/slate/Button";
import { Star } from "@faire/web--source/slate/icons/foundation/Star";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { LoadingSkeleton } from "@faire/web--source/slate/LoadingSkeleton";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { AccessibleStarsDescription } from "@faire/web--source/ui/AccessibleStarsDescription";
import { useResponsiveValue } from "@faire/web--source/ui/hooks/useResponsiveValue";
import { NoTranslate, StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { observer } from "mobx-react";
import * as React from "react";
import { useEffect } from "react";
import styled, { css } from "styled-components";
import { FlagImg } from "@faire/retailer/components/FlagImg";
import { InsiderTileBadgeRenderer } from "@faire/retailer/components/InsiderBadge/InsiderTileBadgeRenderer";
import { withConfigurationConsumer } from "@faire/retailer/components/LeanBrandTileRenderer/__internal__/configurableBrandTileUtils";
import { getOrderMinimum } from "@faire/retailer/components/LeanBrandTileRenderer/brandTileUtils";
import { MaybeBrandReviewTile } from "@faire/retailer/components/MaybeBrandReviewTile/MaybeBrandReviewTile";
import { TrackedLink } from "@faire/retailer/components/TrackedLink";
import { PROMO_HIGHLIGHT } from "@faire/retailer/consts/colors";
import { DEFAULT_BRAND_TILE_PROMO_STYLE } from "@faire/retailer/features/Events/utils";
import { useInsiderState } from "@faire/retailer/hooks/insider/useInsiderState";
import { useAssignSettingUpdateSaleColorCopy, useSettingUpdateSaleColorCopy } from "@faire/retailer/settings/useSettingUpdateSaleColorCopy";
import { BrandStory } from "./BrandStory";
var NameAndMoreBase = function(props) {
    var strictLocalize = useStrictLocalization().strictLocalize;
    var brand = props.brand, promoTextColor = props.promoTextColor, promoTextBackground = props.promoTextBackground, isLoggedIn = props.isLoggedIn, review = props.review, name = props.name, promoText = props.promoText, brandUrl = props.brandUrl, hasNewProducts = props.hasNewProducts, linkLocation = props.linkLocation, token = props.token, onLinkClick = props.onLinkClick, isFirstOrder = props.isFirstOrder, minimum_order_amount = props.minimum_order_amount, configuration = props.configuration, bottomPadding = props.bottomPadding, showMinimum = props.showMinimum, topPadding = props.topPadding, previousOrders = props.previousOrders;
    var reviewCommentMaxLines = useResponsiveValue({
        mobileAndAbove: 5,
        tabletAndAbove: 4
    });
    var _useInsiderState = useInsiderState(), isRetailerInsider = _useInsiderState.isInsider;
    var promo = function(showCategoryBrandStory) {
        return props.promoText || props.brandPromoLabel ? /*#__PURE__*/ _jsx(MarginWrapper, {
            justify: showCategoryBrandStory ? "flex-start" : "center",
            align: "center",
            $addMargin: !!props.brandPromoLabel || !showCategoryBrandStory,
            children: props.brandPromoLabel ? /*#__PURE__*/ _jsx(Promo, {
                text: props.brandPromoLabel.text,
                background: props.promoTextBackground,
                color: props.promoTextColor,
                center: true,
                variant: "paragraphSansRegular"
            }) : /*#__PURE__*/ _jsx(Promo, {
                text: props.promoText,
                background: props.promoTextBackground,
                color: props.promoTextColor,
                center: !showCategoryBrandStory
            })
        }) : null;
    };
    var renderMakerInformation = function() {
        var showCategoryBrandStory = !isLoggedIn && !!(brand === null || brand === void 0 ? void 0 : brand.description);
        var isEventPromo = promoTextColor !== DEFAULT_BRAND_TILE_PROMO_STYLE.promoText || promoTextBackground !== DEFAULT_BRAND_TILE_PROMO_STYLE.lightBackground;
        var shouldShowReview = (review === null || review === void 0 ? void 0 : review.average_rating) && (review === null || review === void 0 ? void 0 : review.total_reviews);
        var shouldCenterText = review ? false : !showCategoryBrandStory;
        var localizedReview = localNum(review === null || review === void 0 ? void 0 : review.average_rating, {
            maximumFractionDigits: 1,
            minimumFractionDigits: 1
        });
        //In both renderBrandNameAndPromo and shouldShowReview session, there is a place to display review.
        //one is checking for brand.avg_brand_review_rating and the other is checking for review.average_rating
        //if both are available, there will be duplicate reviews, we prefer to show the review at shouldShowReview session.
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                renderBrandNameAndPromo(shouldCenterText, !!shouldShowReview),
                shouldShowReview && localizedReview ? /*#__PURE__*/ _jsxs(ReviewWrapper, {
                    children: [
                        /*#__PURE__*/ _jsx(Spacer, {
                            height: "1x"
                        }),
                        /*#__PURE__*/ _jsxs(Row, {
                            children: [
                                /*#__PURE__*/ _jsx(AccessibleStarsDescription, {
                                    rating: localizedReview.toString(),
                                    children: /*#__PURE__*/ _jsxs(Row, {
                                        children: [
                                            /*#__PURE__*/ _jsx(Star, {
                                                fill: "faireBlack",
                                                fontSize: "16px"
                                            }),
                                            /*#__PURE__*/ _jsx(Spacer, {
                                                width: 4
                                            }),
                                            /*#__PURE__*/ _jsx(Typography, {
                                                children: localizedReview
                                            })
                                        ]
                                    })
                                }),
                                /*#__PURE__*/ _jsx(Spacer, {
                                    width: 4
                                }),
                                /*#__PURE__*/ _jsx(Typography, {
                                    color: Core.text.subdued,
                                    children: "("
                                }),
                                /*#__PURE__*/ _jsx(Button, {
                                    variant: "tertiary",
                                    tertiaryColor: Core.text.subdued,
                                    linkTo: brandUrl,
                                    children: localNum(review.total_reviews)
                                }),
                                /*#__PURE__*/ _jsx(Typography, {
                                    color: Core.text.subdued,
                                    children: ")"
                                })
                            ]
                        }),
                        review.review_comment ? /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(Spacer, {
                                    height: "1x"
                                }),
                                /*#__PURE__*/ _jsx(Typography, {
                                    maxLines: reviewCommentMaxLines,
                                    children: wrapStringInQuotes(review.review_comment, strictLocalize)
                                }),
                                /*#__PURE__*/ _jsx(Spacer, {
                                    height: "1x"
                                }),
                                /*#__PURE__*/ _jsx(Typography, {
                                    color: Core.text.subdued,
                                    children: "".concat(review.user_first_name, " (").concat(review.retailer_location, ")")
                                })
                            ]
                        }) : /*#__PURE__*/ _jsx(BrandStory, {
                            name: name,
                            description: brand === null || brand === void 0 ? void 0 : brand.description,
                            showCategoryBrandStory: showCategoryBrandStory,
                            hasPromoText: !!promoText
                        })
                    ]
                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        isEventPromo ? promo(showCategoryBrandStory) : /*#__PURE__*/ _jsx(PaddingWrapper, {
                            children: promo(showCategoryBrandStory)
                        }),
                        /*#__PURE__*/ _jsx(PaddingWrapper, {
                            children: /*#__PURE__*/ _jsx(BrandStory, {
                                name: name,
                                description: brand === null || brand === void 0 ? void 0 : brand.description,
                                showCategoryBrandStory: showCategoryBrandStory,
                                hasPromoText: !!promoText
                            })
                        })
                    ]
                })
            ]
        });
    };
    var renderBrandNameAndPromo = function(shouldCenterText, shouldShowReview) {
        var _brand_region_flag_image;
        var showNewItemsText = hasNewProducts && name && !showMinimum;
        var brandNameLoading = /*#__PURE__*/ _jsx(Row, {
            justify: "center",
            children: /*#__PURE__*/ _jsx(LoadingSkeleton, {
                width: "100px",
                height: "20px"
            })
        });
        var brandMinimumLoading = /*#__PURE__*/ _jsx(Row, {
            justify: "center",
            children: /*#__PURE__*/ _jsx(LoadingSkeleton, {
                width: "110px",
                height: "20px"
            })
        });
        return /*#__PURE__*/ _jsxs(PaddingWrapper, {
            children: [
                /*#__PURE__*/ _jsx(TrackedLink, {
                    token: token,
                    onClick: function() {
                        onLinkClick === null || onLinkClick === void 0 ? void 0 : onLinkClick();
                    },
                    to: linkLocation,
                    children: /*#__PURE__*/ _jsx(Row, {
                        justify: "center",
                        align: "center",
                        style: {
                            position: "relative"
                        },
                        children: /*#__PURE__*/ _jsxs(Column, {
                            style: {
                                width: "100%",
                                position: "relative"
                            },
                            align: "flex-start",
                            children: [
                                /*#__PURE__*/ _jsx(Spacer, {
                                    height: topPadding ? topPadding : "1x"
                                }),
                                name ? /*#__PURE__*/ _jsxs(BrandName, {
                                    align: shouldCenterText ? "center" : "left",
                                    truncate: true,
                                    children: [
                                        (brand === null || brand === void 0 ? void 0 : (_brand_region_flag_image = brand.region_flag_image) === null || _brand_region_flag_image === void 0 ? void 0 : _brand_region_flag_image.url) ? /*#__PURE__*/ _jsx(FlagImg, {
                                            src: brand.region_flag_image.url,
                                            alt: brand.region_flag_image.alt_text
                                        }) : null,
                                        /*#__PURE__*/ _jsx(NoTranslate, {
                                            children: name
                                        })
                                    ]
                                }) : brandNameLoading
                            ]
                        })
                    })
                }),
                /*#__PURE__*/ _jsx(Spacer, {
                    height: 4
                }),
                /*#__PURE__*/ _jsxs(WrappingRow, {
                    justify: shouldCenterText ? "center" : "flex-start",
                    align: "center",
                    children: [
                        !shouldShowReview ? /*#__PURE__*/ _jsx(MaybeBrandReviewTile, {
                            rating: brand === null || brand === void 0 ? void 0 : brand.avg_brand_review_rating
                        }) : null,
                        (isLoggedIn || showMinimum) && !configuration.disableBrandMinimum ? /*#__PURE__*/ _jsx(_Fragment, {
                            children: name ? /*#__PURE__*/ _jsx(Typography, {
                                align: shouldCenterText ? "center" : "start",
                                children: getOrderMinimum(isFirstOrder, minimum_order_amount, strictLocalize)
                            }) : brandMinimumLoading
                        }) : null,
                        showNewItemsText ? /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                isLoggedIn ? /*#__PURE__*/ _jsx(Spacer, {
                                    width: "1x"
                                }) : null,
                                /*#__PURE__*/ _jsx(Typography, {
                                    color: Core.text.subdued,
                                    truncate: true,
                                    align: "center",
                                    children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                        id: "3efSlv",
                                        defaultMessage: [
                                            {
                                                type: 0,
                                                value: "New Items!"
                                            }
                                        ]
                                    })
                                })
                            ]
                        }) : null
                    ]
                }),
                !showNewItemsText && bottomPadding ? /*#__PURE__*/ _jsx(Spacer, {
                    height: 4
                }) : null,
                /*#__PURE__*/ _jsx(WrappingRow, {
                    justify: shouldCenterText ? "center" : "flex-start",
                    children: benefitLabel(shouldCenterText)
                }),
                previousOrders ? /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(Spacer, {
                            height: "1/2x"
                        }),
                        /*#__PURE__*/ _jsx(WrappingRow, {
                            justify: "center",
                            children: /*#__PURE__*/ _jsx(Typography, {
                                align: "center",
                                color: Core.text.subdued,
                                children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                    id: "ACWKD3",
                                    defaultMessage: [
                                        {
                                            type: 6,
                                            value: "previousOrdersNumber",
                                            options: {
                                                one: {
                                                    value: [
                                                        {
                                                            type: 7
                                                        },
                                                        {
                                                            type: 0,
                                                            value: " recent order"
                                                        }
                                                    ]
                                                },
                                                other: {
                                                    value: [
                                                        {
                                                            type: 7
                                                        },
                                                        {
                                                            type: 0,
                                                            value: " recent orders"
                                                        }
                                                    ]
                                                }
                                            },
                                            offset: 0,
                                            pluralType: "cardinal"
                                        }
                                    ],
                                    values: {
                                        previousOrdersNumber: previousOrders
                                    }
                                })
                            })
                        })
                    ]
                }) : null
            ]
        });
    };
    var benefitLabel = function(shouldCenterText) {
        var _props_brand_insider_benefits_benefits, _props_brand_insider_benefits, _props_brand;
        if (props.featuredBrandPromoLabel) {
            return /*#__PURE__*/ _jsx(Promo, {
                text: props.featuredBrandPromoLabel.text,
                background: props.promoTextBackground,
                color: props.promoTextColor,
                center: true,
                variant: "paragraphSansRegular"
            });
        }
        var brandHasInsiderBenefits = !!((_props_brand = props.brand) === null || _props_brand === void 0 ? void 0 : (_props_brand_insider_benefits = _props_brand.insider_benefits) === null || _props_brand_insider_benefits === void 0 ? void 0 : (_props_brand_insider_benefits_benefits = _props_brand_insider_benefits.benefits) === null || _props_brand_insider_benefits_benefits === void 0 ? void 0 : _props_brand_insider_benefits_benefits.length);
        var shouldShowInsiderBadging = isRetailerInsider;
        if (shouldShowInsiderBadging && brandHasInsiderBenefits) {
            var _props_brand1, _props_brand_insider_label, _props_brand2;
            return /*#__PURE__*/ _jsx(StyledInsiderTileBadge, {
                $shouldCenterText: shouldCenterText,
                insiderBenefits: (_props_brand1 = props.brand) === null || _props_brand1 === void 0 ? void 0 : _props_brand1.insider_benefits,
                insiderLabelText: (_props_brand2 = props.brand) === null || _props_brand2 === void 0 ? void 0 : (_props_brand_insider_label = _props_brand2.insider_label) === null || _props_brand_insider_label === void 0 ? void 0 : _props_brand_insider_label.text
            });
        }
        return null;
    };
    return /*#__PURE__*/ _jsx(NameAndMoreInnerWrapper, {
        className: props.className,
        children: renderMakerInformation()
    });
};
export var NameAndMore = observer(withConfigurationConsumer(NameAndMoreBase));
var Promo = function(param) {
    var text = param.text, background = param.background, color = param.color, center = param.center, _param_variant = param.variant, variant = _param_variant === void 0 ? "paragraphSansMedium" : _param_variant, marginTop = param.marginTop, padding = param.padding;
    var isEventPromo = color !== DEFAULT_BRAND_TILE_PROMO_STYLE.promoText || background !== DEFAULT_BRAND_TILE_PROMO_STYLE.lightBackground;
    return isEventPromo ? /*#__PURE__*/ _jsx(PromoWrapper, {
        $background: background,
        $marginTop: isEventPromo ? getSpacing("1x") : marginTop,
        $padding: isEventPromo ? getSpacing(4, "1x") : padding,
        $width: isEventPromo ? "fit-content" : undefined,
        $center: isEventPromo,
        children: /*#__PURE__*/ _jsx(Typography, {
            variant: isEventPromo ? "paragraphSansMedium" : variant,
            align: center ? "center" : undefined,
            color: color,
            children: text !== null && text !== void 0 ? text : null
        })
    }) : /*#__PURE__*/ _jsx(_Fragment, {
        children: /*#__PURE__*/ _jsx(SaleStyledPromoOrDefault, {
            promoText: text,
            center: center,
            color: color
        })
    });
};
var SaleStyledPromoOrDefault = function(param) {
    var promoText = param.promoText, center = param.center, color = param.color;
    var isUpdateSaleColorCopy = useSettingUpdateSaleColorCopy();
    var assignSettingUpdateSaleColorCopy = useAssignSettingUpdateSaleColorCopy();
    useEffect(function() {
        if (promoText) {
            assignSettingUpdateSaleColorCopy();
        }
    }, [
        promoText,
        assignSettingUpdateSaleColorCopy
    ]);
    if (!promoText) {
        return null;
    }
    return isUpdateSaleColorCopy ? /*#__PURE__*/ _jsx(SaleTypography, {
        variant: "labelSansRegular",
        color: Core.text.default,
        truncate: true,
        maxLines: 1,
        style: {
            marginTop: getSpacing("1/2x")
        },
        children: promoText
    }) : /*#__PURE__*/ _jsx(Typography, {
        align: center ? "center" : undefined,
        color: color,
        children: promoText
    });
};
var SaleTypography = styled(Typography).withConfig({
    componentId: "sc-121477e0-0"
})([
    "background-color:",
    ";border-radius:4px;display:inline-block;padding:2px 3px;"
], PROMO_HIGHLIGHT);
var NameAndMoreInnerWrapper = styled.div.withConfig({
    componentId: "sc-121477e0-1"
})([
    "display:flex;flex-direction:column;align-items:stretch;justify-content:space-between;width:100%;padding:0;"
]);
var PaddingWrapper = styled(NameAndMoreInnerWrapper).withConfig({
    componentId: "sc-121477e0-2"
})([
    "padding:0 7.5%;"
]);
var BrandName = styled(Typography).attrs({
    variant: "paragraphSansMedium"
}).withConfig({
    componentId: "sc-121477e0-3"
})([
    "align-items:flex-start;width:100%;"
]);
var WrappingRow = styled(Row).attrs({
    align: "center"
}).withConfig({
    componentId: "sc-121477e0-4"
})([
    "position:relative;flex-wrap:wrap;height:100%;"
]);
var StyledInsiderTileBadge = styled(InsiderTileBadgeRenderer).withConfig({
    componentId: "sc-121477e0-5"
})([
    "text-align:",
    ";padding-top:4px;"
], function(props) {
    return props.$shouldCenterText ? "center" : "start";
});
var PromoWrapper = styled.div.withConfig({
    componentId: "sc-121477e0-6"
})([
    "background:",
    ";padding:",
    ";",
    " margin-top:",
    ";",
    ""
], function(param) {
    var $background = param.$background;
    return $background;
}, function(props) {
    return props.$padding;
}, function(props) {
    return props.$center ? css([
        "margin:0 auto;"
    ]) : undefined;
}, function(props) {
    return props.$marginTop;
}, function(props) {
    return props.$width ? css([
        "width:",
        ";"
    ], props.$width) : undefined;
});
var MarginWrapper = styled(Row).withConfig({
    componentId: "sc-121477e0-7"
})([
    "margin:",
    ";"
], function(props) {
    return props.$addMargin ? "0 7.5%" : "0";
});
var ReviewWrapper = styled(PaddingWrapper).withConfig({
    componentId: "sc-121477e0-8"
})([
    "padding-bottom:7.5%;"
]);
var wrapStringInQuotes = function(message, strictLocalize) {
    return strictLocalize({
        id: "RpKsQx",
        defaultMessage: [
            {
                type: 0,
                value: "\u201C"
            },
            {
                type: 1,
                value: "quoteAsFullyTranslatedSentence"
            },
            {
                type: 0,
                value: "\u201D"
            }
        ]
    }, {
        quoteAsFullyTranslatedSentence: message
    });
};
